'use client';

import * as React from 'react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { usePathname } from 'next/navigation';

import {
  withLocalizedUrls,
  MENU_CONFIG_BASE,
} from '@/lib/menu';
import type { MegaMenuResolved } from '@/lib/menu';
import { MenuId, SectionId, ItemId } from '@/lib/enum';
import { useDict } from '@/app/[lang]/providers/DictionaryProvider';

/** ====== i18n config (harus sinkron dg middleware) ====== */
const LOCALES = ['id', 'en'] as const;
type Locale = (typeof LOCALES)[number];
const DEFAULT_LOCALE: Locale = 'id';
// Kalau kamu hide default-locale di URL, set ke true dan
// pastikan generator href juga mengembalikan path tanpa prefix untuk default.
const HIDE_DEFAULT_LOCALE = false;

type Crumb = { label: string; href?: string };

type Dict = {
  menu?: Record<string, string>;
  section?: Record<string, string>;
  item?: Record<string, string>;
  common?: Record<string, string>;
};

/** Optional manual overrides */
const LABELS: Partial<Record<string, string>> = {
  // [MenuId.AboutUs]: 'Tentang Kami',
};

const normalizePath = (p: string) => {
  let base = (p.split(/[?#]/)[0] || '/') as string;
  if (base.length > 1 && base.endsWith('/')) base = base.slice(0, -1);
  return base;
};

function detectLocaleFromPath(pathname: string): Locale {
  const first = normalizePath(pathname).split('/').filter(Boolean)[0];
  return (LOCALES.find((l) => l === first) ?? DEFAULT_LOCALE) as Locale;
}

function localeHome(locale: Locale) {
  if (HIDE_DEFAULT_LOCALE && locale === DEFAULT_LOCALE) return '/';
  return `/${locale}`;
}

/** Bangun index path → breadcrumb trail dari CONFIG yang SUDAH di-resolve */
function buildIndex(
  cfg: MegaMenuResolved,
  locale: Locale,
  getLabel: (id: string) => string
) {
  const index = new Map<string, Crumb[]>();

  const push = (url: string, trail: Crumb[]) => {
    const key = normalizePath(url);
    const exist = index.get(key);
    if (!exist || trail.length > exist.length) index.set(key, trail);
  };

  const homeHref = localeHome(locale);
  const home: Crumb = { label: getLabel('home') || 'Home', href: homeHref };
  index.set(normalizePath(homeHref), [home]);

  Object.entries(cfg).forEach(([menuKey, sections]) => {
    const menuTrailBase: Crumb[] = [home, { label: getLabel(menuKey) }];

    sections.forEach((section) => {
      // section: union → bisa punya items ATAU url
      const sectionLabel = getLabel(section.id);
      const sectionHref = 'url' in section ? normalizePath(section.url) : undefined;

      const sectionCrumb: Crumb = sectionHref
        ? { label: sectionLabel, href: sectionHref }
        : { label: sectionLabel };

      const trailSection = [...menuTrailBase, sectionCrumb];
      if (sectionHref) push(sectionHref, trailSection);

      if ('items' in section && Array.isArray(section.items)) {
        section.items.forEach((item) => {
          const itemHref = normalizePath(item.url);
          const itemCrumb: Crumb = {
            label: getLabel(item.id),
            href: itemHref,
          };
          const trailItem = [...menuTrailBase, sectionCrumb, itemCrumb];
          push(itemHref, trailItem);
        });
      }
    });
  });

  return index;
}

function findTrail(index: Map<string, Crumb[]>, pathname: string) {
  const path = normalizePath(pathname);
  if (index.has(path)) return index.get(path)!;

  const parts = path.split('/').filter(Boolean);
  for (let i = parts.length; i > 0; i--) {
    const prefix = '/' + parts.slice(0, i).join('/');
    if (index.has(prefix)) return index.get(prefix)!;
  }
  // fallback ke home (entry pertama dengan href sama seperti home)
  for (const [k, trail] of index.entries()) {
    if (trail.length && trail[0]?.href && k === normalizePath(trail[0].href)) {
      return trail;
    }
  }
  return null;
}

export function Breadcrumbs() {
  const pathname = usePathname() || '/';
  const locale = detectLocaleFromPath(pathname);
  const dict = useDict() as Dict;

  const getLabel = React.useCallback(
    (id: MenuId | SectionId | ItemId | string) => {
      const override = LABELS[id as MenuId | SectionId | ItemId];
      if (override) return override;

      const key = String(id);
      return (
        dict?.menu?.[key] ??
        dict?.section?.[key] ??
        dict?.item?.[key] ??
        dict?.common?.[key] ??
        (key === 'home' ? dict?.common?.home : undefined) ??
        key
      );
    },
    [dict]
  );

  // Resolve config dengan URL terlokalisasi (sekali per perubahan locale)
  const RESOLVED = React.useMemo(
    () => withLocalizedUrls(locale, MENU_CONFIG_BASE),
    [locale]
  );

  const index = React.useMemo(
    () => buildIndex(RESOLVED, locale, getLabel),
    [RESOLVED, locale, getLabel]
  );

  const trail = React.useMemo(
    () =>
      findTrail(index, pathname) ?? [
        { label: getLabel('home') || 'Home', href: localeHome(locale) },
      ],
    [index, pathname, locale, getLabel]
  );

  if (!trail?.length) return null;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {trail.map((c, i) => {
          const isLast = i === trail.length - 1;
          return [
            <BreadcrumbItem key={'item-' + i}>
              {c.href && !isLast ? (
                <BreadcrumbLink href={c.href}>{c.label}</BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{c.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>,
            !isLast && <BreadcrumbSeparator key={'sep-' + i} />,
          ];
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}